import React from "react";
import { Box, Checkbox, Skeleton, Button, Tooltip } from "@mui/material";
import { IconArrowUp, IconArrowDown } from "@tabler/icons-react";
import tokens from "../../theme/tokens";
import { getBestScreeningResult, getScreeningData } from "../../../services/screeningService";
import { STAGE, getInitials, formatScore } from "./candidateRow";
import { screenProgress, screenOutcome, isScreened } from "./screeningStatus";
import { formatValue } from "./screeningFields";
import { formatTimestamp, fullTimestamp } from "./metaColumns";
import { statusChip } from "./resumeStatus";

const { color, font, layout, radius, avatarPalette, scoreColor } = tokens;

// Tones for the pipeline-status chip, kept local to the table. `progress` reads as live work,
// `danger` as something the user has to act on (failed or stuck), `muted` as merely queued.
const STATUS_TONE = {
  progress: { bg: color.indigoTint, fg: color.indigo },
  danger: { bg: "#FCEBEA", fg: color.danger },
  muted: { bg: color.borderRow, fg: color.textMuted },
};

/**
 * The pipeline-status affordance shown under a candidate's name while the resume is still moving
 * through upload -> parse -> score, or when it has stalled/failed. Renders nothing for a normal
 * scored row, so an established table looks exactly as it did before.
 */
const PipelineStatus = ({ row, onRetry }) => {
  const chip = statusChip(row);
  if (!chip) return null;
  const tone = STATUS_TONE[chip.tone] || STATUS_TONE.muted;

  return (
    <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}>
      <Tooltip arrow placement="top" title={chip.detail || ""} disableHoverListener={!chip.detail}>
        <Box
          sx={{
            font: `600 10px ${font.sans}`,
            color: tone.fg,
            bgcolor: tone.bg,
            borderRadius: `${radius.chip}px`,
            px: 0.75,
            py: 0.125,
            whiteSpace: "nowrap",
          }}
        >
          {chip.label}
        </Box>
      </Tooltip>
      {chip.retryable && onRetry && (
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            onRetry(row.resumeId);
          }}
          sx={{ minWidth: 0, p: 0, font: `600 10px ${font.sans}`, color: color.indigo }}
        >
          Retry
        </Button>
      )}
    </Box>
  );
};

// The Screening tab trades the score breakdown for call outcome — the columns a
// recruiter actually needs while calls are landing. Its screening_data columns are chosen by the
// user (see ColumnPicker), so the grid is built, not fixed.
/**
 * `minmax(..., Nfr)`, never a bare `1fr`.
 *
 * A bare `1fr` is `minmax(auto, 1fr)`, and the name cell sets `minWidth: 0` so it can ellipsis —
 * which means on a narrow window the flexible tracks get squeezed to nothing and the candidate,
 * the one column the whole table is about, disappears before any of the 76px score columns do.
 * The floor stops that: past it the row overflows and the table scrolls sideways instead.
 */
const CANDIDATE_COL = "minmax(180px,1fr)";

// Email and phone are their own columns here (they used to be a line under the name), so the
// default grid carries two more tracks than the stage-specific ones. The last track is SCREEN: a
// progress chip stacked over the call's outcome, so it needs more than the 90px the outcome
// alone used to take.
const GRID = `36px 44px ${CANDIDATE_COL} minmax(160px,1.2fr) 128px 76px 76px 76px 76px 76px 100px 130px`;
const GRID_SCREENING_BASE = `36px 44px ${CANDIDATE_COL} 130px 70px 100px 76px`;
const SCREENING_COL_WIDTH = "110px";
const GRID_INTERVIEW = `36px 44px ${CANDIDATE_COL} 130px 100px 76px`;

// Created/Updated are opt-in (ColumnPicker) and append to whichever layout is showing, so they are
// the last tracks on every tab rather than a fourth grid variant.
const META_COL_WIDTH = "120px";

const GRID_GAP = 8; // matches `gap: 1` on the header and row grids

/**
 * The width below which the row stops shrinking and the pane starts scrolling.
 *
 * Without it the row box is only as wide as the pane: once the tracks overflow, the cells keep
 * going but the row's background, hover tint and bottom border stop at the pane edge, so a
 * sideways scroll reveals naked columns floating on the page. Derived from the template rather
 * than hand-maintained, because a magic number here silently rots the moment a column is added.
 */
const gridMinWidth = (grid) => {
  const tracks = grid.split(" ");
  const floor = (track) => {
    const min = track.match(/^minmax\((\d+)px/) || track.match(/^(\d+)px$/);
    return min ? Number(min[1]) : 0;
  };
  return tracks.reduce((sum, t) => sum + floor(t), 0) + GRID_GAP * (tracks.length - 1);
};

/** Interview Status from the interview endpoint: completed / pending / scheduled. */
const INTERVIEW_CHIP = {
  completed: { label: "Completed", bg: color.successBg, fg: color.success },
  scheduled: { label: "Scheduled", bg: color.indigoTint, fg: color.indigo },
  pending: { label: "Pending", bg: color.borderRow, fg: color.textSecondary },
};

const STAGE_CHIP = {
  [STAGE.INTERVIEW]: { label: "Interview", bg: color.indigoTint, fg: color.indigo },
  [STAGE.SCREENING]: { label: "Screening", bg: color.purpleBg, fg: color.purple },
  [STAGE.APPLIED]: { label: "Applied", bg: color.borderRow, fg: color.textSecondary },
};

/** Same stage, call finished: "Screening" means the call is still live, "Screened" that it landed. */
const SCREENED_CHIP = { label: "Screened", bg: color.purpleBg, fg: color.purple };

/**
 * Stage is server-authoritative (`ranking.Stage`), so this refines the label, it does not override
 * the stage: only a candidate the server still calls SCREENING can read as "Screened", and only
 * once the call reached a final state. A candidate the server has already advanced to INTERVIEW is
 * never walked back to a screening label by something the client noticed.
 */
const stageChip = (row, screening) =>
  row.stage === STAGE.SCREENING && isScreened(screening) ? SCREENED_CHIP : STAGE_CHIP[row.stage];

/** The console's status pill. `tone` is any `{ label, bg, fg }` — CALL_STATUS, PROGRESS, … */
const Pill = ({ tone }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      maxWidth: "100%",
      font: `600 10px ${font.sans}`,
      textTransform: "uppercase",
      borderRadius: `${radius.chip}px`,
      px: 0.875,
      py: 0.375,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      bgcolor: tone.bg,
      color: tone.fg,
    }}
  >
    {tone.label}
  </Box>
);

/**
 * The SCREEN cell: how far the call got, and — only once it finished — how it went.
 *
 * One component, every tab. The Screening tab used to render its own single chip off `lead_status`
 * and print the literal word "Scheduled" for anything it didn't recognise, which is how a candidate
 * who was never called ended up reading as scheduled. Any new tab that falls through to the default
 * grid gets this for free.
 */
const ScreenCell = ({ screening }) => {
  const progress = screenProgress(screening);
  const outcome = screenOutcome(screening);
  return (
    // alignItems keeps each pill hugging its text — a flex column stretches its children to the
    // full track width by default, which would give two full-width blocks instead of two pills.
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.375,
      }}
    >
      <Pill tone={progress} />
      {outcome && <Pill tone={outcome} />}
    </Box>
  );
};

const headCellSx = {
  font: `600 10.5px ${font.sans}`,
  color: color.textMuted,
  letterSpacing: ".05em",
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
};

const numCellSx = { justifyContent: "flex-end", textAlign: "right" };

/**
 * Tint is derived from the resume id, not the row index — otherwise a candidate's
 * avatar colour would change every time the table is sorted or filtered.
 */
const tintFor = (resumeId) => {
  const s = String(resumeId ?? "");
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return avatarPalette[Math.abs(hash) % avatarPalette.length];
};

const SortHeader = ({ label, sortKey, sort, onSort }) => {
  const active = sort.key === sortKey;
  return (
    <Box
      onClick={() => onSort(sortKey)}
      sx={{
        ...headCellSx,
        ...numCellSx,
        gap: 0.25,
        cursor: "pointer",
        color: active ? color.textSecondary : color.textMuted,
        "&:hover": { color: color.textSecondary },
      }}
    >
      {label}
      {active &&
        (sort.dir === "asc" ? (
          <IconArrowUp size={11} stroke={2} />
        ) : (
          <IconArrowDown size={11} stroke={2} />
        ))}
    </Box>
  );
};

const CandidateTable = ({
  rows,
  loading,
  sort,
  onSort,
  selectedIds,
  onToggle,
  onToggleAll,
  onOpenCandidate,
  screeningByResumeId,
  screeningView,
  // Already filtered to what should render (selected ∩ non-empty) by the Console — the table
  // doesn't decide visibility, it just renders what it's given.
  screeningColumns = [],
  // The Created/Updated columns the user has switched on, as META_FIELDS entries. Unlike
  // screeningColumns these are the same two on every tab — the row's own timestamps, not data the
  // vendor may or may not have sent.
  metaColumns = [],
  interviewByResumeId,
  interviewView,
  onUpload,
  onRetry,
}) => {
  const baseGrid = screeningView
    ? [GRID_SCREENING_BASE, ...screeningColumns.map(() => SCREENING_COL_WIDTH)].join(" ")
    : interviewView
    ? GRID_INTERVIEW
    : GRID;

  const grid = [baseGrid, ...metaColumns.map(() => META_COL_WIDTH)].join(" ");

  // Only the default view has Email/Phone columns, so only there is the phone-under-the-name
  // redundant. On Screening/Interview the number is the point — it stays.
  const defaultView = !screeningView && !interviewView;

  // The same floor on the header and every row — a mismatch here misaligns the columns from
  // their headings the moment the table scrolls sideways.
  const minWidth = gridMinWidth(grid);

  const selectedOnScreen = rows.filter((r) => selectedIds.has(r.resumeId)).length;
  const allSelected = rows.length > 0 && selectedOnScreen === rows.length;
  const someSelected = selectedOnScreen > 0;

  if (loading) {
    return (
      <Box sx={{ px: 2.25, py: 1 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} height={layout.rowHeight} sx={{ bgcolor: color.borderRow }} />
        ))}
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box sx={{ px: 2.25, py: 6, textAlign: "center" }}>
        <Box sx={{ font: `400 13px ${font.sans}`, color: color.textSecondary, mb: 2 }}>
          No candidates here yet.
        </Box>
        {onUpload && (
          <Button variant="contained" onClick={onUpload}>
            Upload resumes
          </Button>
        )}
      </Box>
    );
  }

  return (
    // overflowX: once the tracks hit their floors the row is wider than the pane, and the choice is
    // scroll or clip. Clipping silently eats the right-hand columns; scrolling is honest.
    <Box sx={{ flex: 1, overflowY: "auto", overflowX: "auto", minHeight: 0 }}>
      {/* Below md the table becomes a stack of cards (see the mobile block further
          down); the header row is meaningless there. */}
      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: grid,
          minWidth,
          gap: 1,
          px: 2.25,
          py: 1,
          position: "sticky",
          top: 0,
          bgcolor: color.bg,
          zIndex: 1,
          borderBottom: `1px solid ${color.borderRow}`,
        }}
      >
        {/* Select-all acts on the rows on screen — this tab, this search. Indeterminate when the
            selection is partial, so it never claims a state it isn't in. */}
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={() => onToggleAll?.(rows, !allSelected)}
          inputProps={{ "aria-label": allSelected ? "Deselect all" : "Select all" }}
          sx={{ p: 0.5 }}
        />
        <Box sx={headCellSx}>Rank</Box>
        <Box sx={headCellSx}>Candidate</Box>
        {screeningView ? (
          <>
            <Box sx={headCellSx}>Call status</Box>
            <Box sx={{ ...headCellSx, ...numCellSx }}>Attempts</Box>
            <Box sx={{ ...headCellSx, ...numCellSx }}>Screen score</Box>
            <SortHeader label="Total" sortKey="total" sort={sort} onSort={onSort} />
            {/* Display-only: raw strings don't sort numerically ("9,00,000" vs "500000000"
                compares as text and lies), so these deliberately get no SortHeader. */}
            {screeningColumns.map((field) => (
              <Box key={field.key} sx={{ ...headCellSx, ...numCellSx }}>
                {field.label}
              </Box>
            ))}
          </>
        ) : interviewView ? (
          <>
            <Box sx={headCellSx}>Interview</Box>
            <Box sx={{ ...headCellSx, ...numCellSx }}>Int. score</Box>
            <SortHeader label="Total" sortKey="total" sort={sort} onSort={onSort} />
          </>
        ) : (
          <>
            <Box sx={headCellSx}>Email</Box>
            <Box sx={headCellSx}>Phone</Box>
            <SortHeader label="Total" sortKey="total" sort={sort} onSort={onSort} />
            <SortHeader label="Skills" sortKey="skills" sort={sort} onSort={onSort} />
            <Box sx={{ ...headCellSx, ...numCellSx }}>Exp</Box>
            <Box sx={{ ...headCellSx, ...numCellSx }}>Edu</Box>
            <Box sx={{ ...headCellSx, ...numCellSx }}>P2P</Box>
            <Box sx={headCellSx}>Stage</Box>
            <Box sx={headCellSx}>Screen</Box>
          </>
        )}

        {/* Deliberately no SortHeader: sortRows subtracts its operands, and these are ISO strings.
            Sorting by date needs a comparator that doesn't exist yet — an inert ↕ glyph that
            scrambled the table would be worse than no affordance at all. */}
        {metaColumns.map((field) => (
          <Box key={field.key} sx={headCellSx}>
            {field.label}
          </Box>
        ))}
      </Box>

      {rows.map((row) => {
        const selected = selectedIds.has(row.resumeId);
        const tint = tintFor(row.resumeId);
        const screening = screeningByResumeId?.[row.resumeId];
        const chip = stageChip(row, screening);
        // A candidate can be called several times. The score that represents them is
        // their BEST completed attempt, not the most recent one — someone who scored 8.6
        // and was later called back for a 20-second no-answer has not become worse.
        const rating = getBestScreeningResult(screening?.results)?.interview_rating;
        const screenScore = rating === null || rating === undefined ? null : rating;

        // Every call placed, no-answers and callbacks included — not just the ones that produced a
        // score. A candidate on attempt 4 with nothing to show is exactly who a recruiter needs to
        // spot, and averaging that away into "no score yet" is how they stay invisible.
        const attempts = screening?.results?.length ?? 0;

        const interview = interviewByResumeId?.[row.resumeId];
        const interviewChip = INTERVIEW_CHIP[String(interview?.["Interview Status"] || "").toLowerCase()];
        const iScore = interview?.["Total Interview score"];
        const interviewScore = iScore === null || iScore === undefined ? null : iScore;

        return (
          <React.Fragment key={row.resumeId}>
          {/* Mobile card */}
          <Box
            onClick={() => onOpenCandidate(row)}
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 1.5,
              mx: 1.5,
              mb: 1,
              px: 1.75,
              py: 1.5,
              minHeight: layout.touchRowHeight,
              border: `1px solid ${color.border}`,
              borderRadius: `${radius.fileRow + 2}px`,
              bgcolor: selected ? color.rowTint : color.surface,
            }}
          >
            <Checkbox
              size="small"
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggle(row)}
              sx={{ p: 0.5 }}
            />
            <Box sx={{ font: `400 12px ${font.mono}`, color: color.textMuted }}>
              {String(row.rank ?? "").padStart(2, "0")}
            </Box>
            <Box
              sx={{
                width: 32,
                height: 32,
                flex: "none",
                display: "grid",
                placeItems: "center",
                borderRadius: `${radius.control}px`,
                bgcolor: tint.bg,
                color: tint.fg,
                font: `600 11px ${font.sans}`,
              }}
            >
              {getInitials(row.name)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  font: `500 13px ${font.sans}`,
                  color: color.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.name}
              </Box>
              {row.phone && (
                <Box
                  sx={{
                    mt: 0.25,
                    font: `400 10.5px ${font.mono}`,
                    color: color.textSecondary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.phone}
                </Box>
              )}
              <Box sx={{ mt: 0.25, font: `400 10.5px ${font.sans}`, color: color.textMuted }}>
                {chip.label}
                {screening?.label ? ` · ${screening.label}` : ""}
              </Box>
              <PipelineStatus row={row} onRetry={onRetry} />
            </Box>
            <Box sx={{ font: `600 13px ${font.mono}`, color: scoreColor(row.total) }}>
              {formatScore(row.total)}
            </Box>
          </Box>

          {/* Desktop row */}
          <Box
            sx={{
              display: { xs: "none", md: "grid" },
              gridTemplateColumns: grid,
              minWidth,
              gap: 1,
              alignItems: "center",
              px: 2.25,
              minHeight: layout.rowHeight,
              borderBottom: `1px solid ${color.borderRow}`,
              bgcolor: selected ? color.rowTint : "transparent",
              transition: "background-color 100ms ease",
              "&:hover": { bgcolor: color.rowTint },
            }}
          >
            <Checkbox
              size="small"
              checked={selected}
              onChange={() => onToggle(row)}
              sx={{ p: 0.5 }}
            />

            <Box sx={{ font: `400 12px ${font.mono}`, color: color.textMuted }}>
              {String(row.rank ?? "").padStart(2, "0")}
            </Box>

            <Box
              onClick={() => onOpenCandidate(row)}
              sx={{ display: "flex", alignItems: "center", gap: 1.25, cursor: "pointer", minWidth: 0 }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  flex: "none",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: `${radius.button}px`,
                  bgcolor: tint.bg,
                  color: tint.fg,
                  font: `600 9.5px ${font.sans}`,
                }}
              >
                {getInitials(row.name)}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    font: `500 13px ${font.sans}`,
                    color: color.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.name}
                </Box>
                {!defaultView && row.phone && (
                  <Box
                    sx={{
                      font: `400 10.5px ${font.mono}`,
                      color: color.textSecondary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.phone}
                  </Box>
                )}
                <PipelineStatus row={row} onRetry={onRetry} />
              </Box>
            </Box>

            {screeningView ? (
              <>
                <ScreenCell screening={screening} />
                <Box
                  sx={{
                    font: `400 12px ${font.mono}`,
                    textAlign: "right",
                    // Repeat calls are a signal, not a score: >1 means we've had to chase this
                    // candidate, so it's darkened rather than left as muted furniture.
                    color: attempts > 1 ? color.text : color.textMuted,
                  }}
                >
                  {attempts}
                </Box>
                <Box
                  sx={{
                    font: `500 12.5px ${font.mono}`,
                    textAlign: "right",
                    // Same band scale as every other score. This used to be its own
                    // green-above-7 / red-below rule, which would now contradict the
                    // table: a 7.4 would read red here and blue two columns over.
                    color: scoreColor(screenScore),
                  }}
                >
                  {formatScore(screenScore)}
                </Box>
                <Box
                  sx={{
                    font: `600 13px ${font.mono}`,
                    color: scoreColor(row.total),
                    textAlign: "right",
                  }}
                >
                  {formatScore(row.total)}
                </Box>
                {screeningColumns.map((field) => {
                  const value = getScreeningData(screening?.results)?.[field.key];
                  const displayValue = formatValue(value);
                  return (
                    <Tooltip
                      key={field.key}
                      arrow
                      placement="top"
                      enterDelay={250}
                      title={`${field.label}: ${displayValue || "—"}`}
                    >
                      <Box
                        sx={{
                          font: `400 12px ${font.mono}`,
                          color: color.textSecondary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          textAlign: "right",
                        }}
                      >
                        {displayValue}
                      </Box>
                    </Tooltip>
                  );
                })}
              </>
            ) : interviewView ? (
              <>
                <Box>
                  {interviewChip ? (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        font: `600 10px ${font.sans}`,
                        textTransform: "uppercase",
                        borderRadius: `${radius.chip}px`,
                        px: 0.875,
                        py: 0.375,
                        bgcolor: interviewChip.bg,
                        color: interviewChip.fg,
                      }}
                    >
                      {interviewChip.label}
                    </Box>
                  ) : (
                    <Box sx={{ font: `400 12px ${font.sans}`, color: color.textMuted }}>
                      Not invited
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    font: `500 12.5px ${font.mono}`,
                    textAlign: "right",
                    color: scoreColor(interviewScore),
                  }}
                >
                  {formatScore(interviewScore)}
                </Box>
                <Box
                  sx={{
                    font: `600 13px ${font.mono}`,
                    color: scoreColor(row.total),
                    textAlign: "right",
                  }}
                >
                  {formatScore(row.total)}
                </Box>
              </>
            ) : (
              <>
                {/* A missing email is flagged, not blank: interview invites go out by email, so a
                    candidate without one silently drops out of the flow. Same rule as the panel. */}
                <Box
                  title={row.email || undefined}
                  sx={{
                    font: `400 12px ${font.sans}`,
                    color: row.email ? color.textSecondary : color.danger,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.email || "No email"}
                </Box>
                <Box
                  title={row.phone || undefined}
                  sx={{
                    font: `400 12px ${font.mono}`,
                    color: row.phone ? color.textSecondary : color.textMuted,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.phone || "—"}
                </Box>
                <Box
                  sx={{
                    font: `600 13px ${font.mono}`,
                    color: scoreColor(row.total),
                    textAlign: "right",
                  }}
                >
                  {formatScore(row.total)}
                </Box>
                {["skills", "exp", "edu", "p2p"].map((k) => (
                  <Box
                    key={k}
                    sx={{
                      font: `400 12.5px ${font.mono}`,
                      // Sub-scores use the same band scale as TOTAL, one weight lighter so
                      // the headline still leads the row.
                      color: scoreColor(row[k]),
                      textAlign: "right",
                    }}
                  >
                    {formatScore(row[k])}
                  </Box>
                ))}

                <Box>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      font: `600 10px ${font.sans}`,
                      textTransform: "uppercase",
                      borderRadius: `${radius.chip}px`,
                      px: 0.875,
                      py: 0.375,
                      bgcolor: chip.bg,
                      color: chip.fg,
                    }}
                  >
                    {chip.label}
                  </Box>
                </Box>

                <ScreenCell screening={screening} />
              </>
            )}

            {metaColumns.map((field) => {
              const iso = row[field.rowKey];
              return (
                <Tooltip
                  key={field.key}
                  arrow
                  placement="top"
                  enterDelay={250}
                  title={`${field.label}: ${fullTimestamp(iso)}`}
                >
                  <Box
                    sx={{
                      font: `400 12px ${font.mono}`,
                      // Muted when absent: a resume that predates the migration has no creation
                      // time, and that em-dash is a fact about our history, not about the candidate.
                      color: iso ? color.textSecondary : color.textMuted,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {formatTimestamp(iso)}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default CandidateTable;
